import clsx from "clsx";
import * as yup from "yup";
import { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { FormProvider, SubmitHandler } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { getWeekYear } from "date-fns";

import {
  BRAND_NAME,
  CURRENCY,
  DELIVERY_INTERVALS,
  DeliveryInterval,
  TIMEBLOCK_TYPE_IDS,
  config,
} from "@chef/constants";
import { isEmptyArray, isNotEmptyArray } from "@chef/utils/array";
import { isEqualStrings } from "@chef/utils/equal";
import { isValidTelephoneNumber } from "@chef/utils/telephoneValidation";
import {
  api,
  AppDispatch,
  getStore,
  selectFirstDeliveryTimeblockId,
  selectSignupDeliveryData,
  selectSignupGiftcard,
  selectSignupPersonalData,
  setFirstDeliveryTimeblockId,
  setSignupDeliveryData,
  TimeblocksByRangeWithGeoQuery,
  updateSignupDeliveryData,
  updateSignupPersonalData,
  useLazyTimeblocksByRangeWithGeoQuery,
  useLazyValidateTelephoneInUseQuery,
  useTimeblocksQuery,
  validateApi,
} from "@chef/state-management";
import { AddressForm, FlexWrapper } from "@chef/smart-components";
import { Input, SignupProgress } from "@chef/components";
import { useExtendedForm, useIntercom } from "@chef/hooks";
import {
  formatTimeInterval,
  getWeek,
  isValidPostalCode,
  yupCacheAllTests,
} from "@chef/helpers";
import { AppShell } from "@chef/feature-app-shell";
import { BlockContent, ISection } from "@chef/feature-sanity";
import { toØre } from "@chef/utils/currency";
import { getAgeFromSwedishSSN } from "@chef/utils/ssn";
import { isEven, isOdd } from "@chef/utils/parity";
import { useAppSelector, useTrack } from "@chef/state-management/hooks";

import type { SignupScheduler } from "@chef/types/payment";

import { filterAvailableWeeklyTimeblocks, getCity } from "../utils";
import { useTrackingData } from "../hooks/useTrackingData";
import { Header } from "../components/Header";
import { DeliveryInformation } from "../components/DeliveryInformation";
import { CustomerExistsModal } from "../components/CustomerExistsModal";
import { CheckoutBar } from "../components/CheckoutBar";
import { useSignupSession } from "../hooks/useSignupSession";

import { intl } from "./SignupCreateDeliveryDetails.Intl";

export type IFormInput = yup.InferType<typeof schema>;

interface SanityAdressPageProps {
  sanityAddressContent?: ISection | null;
  deliveryDetailsModalContent?: ISection | null;
}

type ErrorResponse = { data: { redirectUri: string } };

const postalCodeCache: Map<
  string,
  TimeblocksByRangeWithGeoQuery["timeblocksByRangeWithGeo"]["weeks"]
> = new Map();

const isDifferentDeliveryFrequencyParity = (
  frequency: DeliveryInterval,
  week: number,
) => {
  if (frequency === DELIVERY_INTERVALS.EVERY_WEEK) {
    return false;
  }

  if (frequency === DELIVERY_INTERVALS.EVEN_WEEKS) {
    return !isEven(week);
  }

  if (frequency === DELIVERY_INTERVALS.ODD_WEEKS) {
    return !isOdd(week);
  }

  return false;
};

const createSignupSchedulers = (
  firstDeliveryTimeblockId?: number,
  timeblockId?: number,
  firstDelivery?: string,
  frequency?: DeliveryInterval,
) => {
  const schedulers: SignupScheduler[] = [];

  if (
    !firstDeliveryTimeblockId ||
    !timeblockId ||
    !firstDelivery ||
    !frequency
  ) {
    return schedulers;
  }

  const firstDeliveryWeek = firstDelivery
    ? getWeek(new Date(firstDelivery))
    : null;

  const firstDeliveryYear = firstDelivery
    ? getWeekYear(new Date(firstDelivery))
    : null;

  if (!firstDeliveryWeek || !firstDeliveryYear) {
    return schedulers;
  }

  if (timeblockId !== firstDeliveryTimeblockId) {
    schedulers.push({
      week: firstDeliveryWeek,
      year: firstDeliveryYear,
      timeblockId: firstDeliveryTimeblockId,
    });
  } else if (isDifferentDeliveryFrequencyParity(frequency, firstDeliveryWeek)) {
    schedulers.push({
      week: firstDeliveryWeek,
      year: firstDeliveryYear,
      timeblockId: firstDeliveryTimeblockId,
    });
  }

  return schedulers;
};

export const SignupCreateDeliveryDetails = ({
  sanityAddressContent,
  deliveryDetailsModalContent,
}: SanityAdressPageProps) => {
  useIntercom(false);

  const deliveryDetailsRef = useRef<HTMLDivElement>(null);

  const dispatch = useDispatch<AppDispatch>();
  const track = useTrack();

  const [getTimeblocksByRangeWithGeo] = useLazyTimeblocksByRangeWithGeoQuery();

  const [availableWeekAndYearTimeblocks, setAvailableWeekAndYearTimeblocks] =
    useState<
      TimeblocksByRangeWithGeoQuery["timeblocksByRangeWithGeo"]["weeks"]
    >([]);

  const [noDeliveryAvailable, setNoDeliveryAvailable] = useState(false);
  const [city, setCity] = useState("");
  const [telephoneInUse, setTelephoneInUse] = useState(false);

  const coupon = useAppSelector((state) => state.signupRtk.coupon);
  const giftcard = useSelector(selectSignupGiftcard);

  const { firstName, lastName, ssn, telephone } = useSelector(
    selectSignupPersonalData,
  );

  const {
    postalCode,
    address,
    streetName,
    streetNo,
    letterExtra,
    apartmentName,
    flatNo,
    floorNo,
    postalCodeCity,
    validated,
    addressUUID,
    addressCode,
    longitude,
    latitude,
    addressDigitalKeyProviderId,
    doorCode,
    deliveryInstructionsToDriver,
    firstDelivery,
    frequency,
    deliveryTimeblock,
    requiresPhysicalKey,
  } = useSelector(selectSignupDeliveryData);

  const firstDeliveryTimeblockId = useSelector(selectFirstDeliveryTimeblockId);

  const methods = useExtendedForm<IFormInput>({
    defaultValues: {
      ssn,
      firstName,
      lastName,
      streetName,
      streetNo,
      letterExtra,
      apartmentName,
      flatNo,
      floorNo,
      validated,
      addressUUID,
      addressCode,
      longitude,
      latitude,
      addressDigitalKeyProviderId,
      address,
      postalCode,
      doorCode,
      telephone,
      deliveryInstructionsToDriver,

      requiresPhysicalKey,

      validatedAddress: {},
      apartment: {},
    },
    resolver: yupResolver(schema),
    mode: "all",
  });
  const {
    register,
    handleSubmit,
    formValues,
    formState: { isSubmitting, isValid, errors },
    getValues,
  } = methods;

  const apartments = formValues.validatedAddress?.apartments;

  const isApartmentAddress = isNotEmptyArray(apartments);

  const { data: timeblocksQuery } = useTimeblocksQuery(
    {
      postalCode: getValues("postalCode"),
      timeblockTypeId: TIMEBLOCK_TYPE_IDS.NORMAL,
    },
    {
      skip: !isValidPostalCode(getValues("postalCode")),
    },
  );

  const { updateSignupSession, signupSession } = useSignupSession();

  const firstDeliveryOptions: TimeblocksByRangeWithGeoQuery["timeblocksByRangeWithGeo"]["weeks"][number]["timeblocks"] =
    [];

  const availableTimeblocks = timeblocksQuery?.timeblocksData?.timeblocks || [];

  let initialDeliveryTimeblockId = availableTimeblocks.find(
    (tb) => tb.isDefault,
  )?.timeblockId;

  if (!initialDeliveryTimeblockId) {
    initialDeliveryTimeblockId = availableTimeblocks[0]?.timeblockId;
  }

  useEffect(() => {
    if (!initialDeliveryTimeblockId) {
      return;
    }

    const isFirstDeliveryHasNotBeenSet = isEmptyArray(
      availableWeekAndYearTimeblocks.filter((timeblocks) =>
        timeblocks.timeblocks.find(
          (tb) => tb.firstDeliveryDate === firstDelivery,
        ),
      ),
    );

    if (
      isNotEmptyArray(availableWeekAndYearTimeblocks) &&
      (getValues("postalCode") !== postalCode || isFirstDeliveryHasNotBeenSet)
    ) {
      availableWeekAndYearTimeblocks.forEach((week) => {
        week.timeblocks.forEach((block) => {
          firstDeliveryOptions.push(block);
        });
      });

      let initialFirstDelivery = firstDeliveryOptions?.find(
        (tb) => tb.isDefault,
      );

      if (!initialFirstDelivery) {
        initialFirstDelivery = firstDeliveryOptions?.[0];
      }

      dispatch(
        setSignupDeliveryData({
          address,
          postalCode,
          postalCodeCity: city,
          doorCode,
          streetName,
          streetNo,
          letterExtra,
          apartmentName,
          flatNo,
          floorNo,
          validated,
          addressUUID,
          addressCode,
          longitude,
          latitude,
          addressDigitalKeyProviderId,
          deliveryInstructionsToDriver,
          firstDelivery: initialFirstDelivery?.deliveryDate,
          frequency,
          deliveryTimeblock: initialDeliveryTimeblockId,
          requiresPhysicalKey,
        }),
      );

      dispatch(setFirstDeliveryTimeblockId(+initialFirstDelivery?.timeblockId));
      //reset the first delivery and timeblock redux state if the postal code is changed
    } else if (getValues("postalCode").length !== intl.LENGTH_OF_POSTALCODE) {
      dispatch(
        updateSignupDeliveryData({
          firstDelivery: "",
          frequency: 10,
          deliveryTimeblock: 0,
        }),
      );

      dispatch(setFirstDeliveryTimeblockId(0));
    }
  }, [availableWeekAndYearTimeblocks, postalCode, initialDeliveryTimeblockId]);

  const [validateTelephoneInUse] = useLazyValidateTelephoneInUseQuery();

  const handleNewPostalCodeData = (
    data: TimeblocksByRangeWithGeoQuery["timeblocksByRangeWithGeo"]["weeks"],
  ) => {
    const cityFromTimeblocks = getCity(data);

    setCity(cityFromTimeblocks as string);
    setNoDeliveryAvailable(!cityFromTimeblocks);

    const filteredAvailableWeeklyTimeblocks =
      filterAvailableWeeklyTimeblocks(data);

    setAvailableWeekAndYearTimeblocks(filteredAvailableWeeklyTimeblocks);
  };

  const checkPostalCode = useCallback(
    async (postalCode: string, latitude?: number, longitude?: number) => {
      const cacheKey = `${postalCode}-${latitude}-${longitude}`;
      const cachedData = postalCodeCache.get(cacheKey);

      if (cachedData) {
        handleNewPostalCodeData(cachedData);
      } else {
        try {
          const { data: timeblocksByRangeWithGeoQuery } =
            await getTimeblocksByRangeWithGeo({
              postalCode,
              timeblockTypeId: TIMEBLOCK_TYPE_IDS.NORMAL,
              range: 8,
              latitude,
              longitude,
            });

          if (!timeblocksByRangeWithGeoQuery) {
            setAvailableWeekAndYearTimeblocks([]);
            setCity("");

            throw new Error("Query failed");
          }

          if (
            latitude &&
            longitude &&
            timeblocksByRangeWithGeoQuery.timeblocksByRangeWithGeo
              .isGeoRestricted &&
            !timeblocksByRangeWithGeoQuery.timeblocksByRangeWithGeo
              .deliveryAvailable
          ) {
            setAvailableWeekAndYearTimeblocks([]);
            setNoDeliveryAvailable(true);
            setCity("");

            throw new Error("Geo restricted address");
          }

          const timeblocksByRangeWithGeo =
            timeblocksByRangeWithGeoQuery?.timeblocksByRangeWithGeo.weeks.map(
              (r) => ({
                ...r,
                timeblocks: r.timeblocks.filter((t) =>
                  isEqualStrings(t.timeblockTypeId, TIMEBLOCK_TYPE_IDS.NORMAL),
                ),
              }),
            ) || [];

          if (
            postalCode.length === intl.LENGTH_OF_POSTALCODE &&
            isEmptyArray(timeblocksByRangeWithGeo)
          ) {
            console.error("no timeblocks");

            setAvailableWeekAndYearTimeblocks([]);
            setNoDeliveryAvailable(true);
            setCity("");

            throw new Error("No timeblocks returned");
          }

          postalCodeCache.set(cacheKey, timeblocksByRangeWithGeo);
          handleNewPostalCodeData(timeblocksByRangeWithGeo);

          track("postalCodeChecked", {
            affiliation: "Frontend process",
            postal_code_id: +postalCode,
            postal_code_valid: true,
          });
        } catch (error) {
          track("postalCodeChecked", {
            affiliation: "Frontend process",
            postal_code_id: +postalCode,
            postal_code_valid: false,
          });
        }
      }
    },
    [getTimeblocksByRangeWithGeo, track],
  );

  const handlePostalCodeChange = useCallback(
    async (postalCode: string, latitude?: number, longitude?: number) => {
      if (isValidPostalCode(getValues("postalCode"))) {
        checkPostalCode(postalCode, latitude, longitude);
      } else {
        setAvailableWeekAndYearTimeblocks([]);
        setCity("");
      }
    },
    [checkPostalCode, getValues],
  );

  useEffect(() => {
    if (!formValues.postalCode && availableWeekAndYearTimeblocks.length > 0) {
      setAvailableWeekAndYearTimeblocks([]);
      return;
    }

    if (!formValues.postalCode || !formValues.validatedAddress) {
      setNoDeliveryAvailable(false);
      return;
    }

    handlePostalCodeChange(
      formValues.postalCode,
      formValues.validatedAddress.latitude,
      formValues.validatedAddress.longitude,
    );
  }, [
    availableWeekAndYearTimeblocks.length,
    formValues.postalCode,
    formValues.validatedAddress,
    handlePostalCodeChange,
  ]);

  // Load timeblocks if postal code is already filled in
  useEffect(() => {
    if (postalCode) {
      checkPostalCode(postalCode);
    }
  }, []); // We explicitly don't want to add postalCode here, as we only want to run this when the component mounts

  const trackingData = useTrackingData();

  const onSubmit: SubmitHandler<IFormInput> = async (data) => {
    dispatch(
      updateSignupPersonalData({
        ssn: data.ssn,
        firstName: data.firstName,
        lastName: data.lastName,
        telephone: data.telephone,
      }),
    );

    dispatch(
      updateSignupDeliveryData({
        address: data.validatedAddress?.addressName || address,
        postalCode: data.postalCode,
        postalCodeCity: city,
        doorCode: data.doorCode,
        deliveryInstructionsToDriver: data.deliveryInstructionsToDriver,
        requiresPhysicalKey: data.requiresPhysicalKey,
        streetName: data.validatedAddress?.streetName,
        streetNo: data.validatedAddress?.streetNumber
          ? +data.validatedAddress?.streetNumber
          : undefined,
        letterExtra: data.validatedAddress?.letterExtra,
        apartmentName: data.apartment?.name || undefined,
        flatNo: data.apartment?.door,
        floorNo: data.apartment?.floor || undefined,
        validated: new Date().toUTCString(),
        addressUUID: data.validatedAddress?.addressGuid,
        addressCode: data.validatedAddress?.addressCode,
        longitude: data.validatedAddress?.longitude,
        latitude: data.validatedAddress?.latitude,
        addressDigitalKeyProviderId:
          data.validatedAddress?.digitalKeyProvider
            ?.addressDigitalKeyProviderId,
      }),
    );

    const timeblock = availableWeekAndYearTimeblocks
      .flatMap(({ timeblocks }) => timeblocks)
      .find(({ timeblockId }) => timeblockId === deliveryTimeblock);

    const timeblock_interval = formatTimeInterval(
      timeblock?.from,
      timeblock?.to,
      "h",
      "-",
    );

    let total = 0;

    const deliveryFee = toØre(+(timeblock?.customerFee || 0));

    const sumOfItems =
      signupSession?.basket?.items?.reduce(
        (acc, cur) => acc + (cur?.price || 0),
        0,
      ) || 0;

    total += sumOfItems + deliveryFee;

    const giftcardAmount = toØre(giftcard?.amount || 0);

    let discountAmount = 0;
    if (coupon && coupon.couponCode) {
      if (coupon.amountType === 2 || coupon.type === 2) {
        discountAmount = toØre(coupon.amount);
      } else {
        discountAmount = Math.round((coupon.amount / 100) * total);
      }
    }

    const net = total - discountAmount - giftcardAmount;
    const vat = Math.round(net * config.vatRate);

    const schedulers = createSignupSchedulers(
      firstDeliveryTimeblockId,
      deliveryTimeblock,
      firstDelivery,
      frequency as DeliveryInterval,
    );

    const fallbackAddress = !data.isHealthCheckSuccess
      ? data.address
      : undefined;

    try {
      await updateSignupSession({
        subject: {
          firstName: data.firstName,
          lastName: data.lastName,
          telephone: data.telephone,
          nin: data.ssn,
        },
        logistics: {
          deliveryInterval: frequency as 10 | 20 | 30,
          timeblockId: deliveryTimeblock,
          firstDeliveryDate: firstDelivery,
          address: {
            postalCode: data.postalCode,
            postalArea: city,
            doorCode: data.doorCode,
            instructionsToDriver: data.deliveryInstructionsToDriver,

            streetName: data.validatedAddress?.streetName || fallbackAddress,
            streetNo: data.validatedAddress?.streetNumber,
            apartmentName: data.apartment?.name || undefined,
            letterExtra: data.validatedAddress?.letterExtra,
            addressName: data.validatedAddress?.addressName,

            flatNo: data.apartment?.door || undefined,
            floorNo: data.apartment?.floor || undefined,

            addressUUID: data.validatedAddress?.addressGuid,
            addressCode: data.validatedAddress?.addressCode,
            longitude: data.validatedAddress?.longitude,
            latitude: data.validatedAddress?.latitude,
            addressDigitalKeyProviderId:
              data.validatedAddress?.digitalKeyProvider
                ?.addressDigitalKeyProviderId,

            requiresPhysicalKey: data.requiresPhysicalKey,

            validated: fallbackAddress ? null : new Date().toUTCString(),
          },
          schedulers,
        },
        basket: {
          _meta: {
            deliveryFee,
            net,
            credit: giftcardAmount,
            discount: discountAmount,
            vat,
            total,
          },
        },
      }).unwrap();
      track("personalInformationSubmitted", {
        affiliation: "Frontend process",
        currency: CURRENCY,
        coupon: trackingData.coupon,
        discount: trackingData.discount,
        products: trackingData.products,
        timeblock_day: timeblock?.weekday,
        timeblock_id: deliveryTimeblock,

        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        timeblock_interval,
      });
    } catch (error) {
      const err = error as ErrorResponse;
      if (err.data.redirectUri) {
        window.location.href = err.data.redirectUri;
      }
    }
  };

  useEffect(() => {
    const fetch = async () => {
      const { telephone } = formValues;
      if (
        telephone === undefined ||
        telephone.length !== intl.LENGTH_OF_TELEPHONE_NUMBER
      ) {
        return;
      }

      try {
        await validateTelephoneInUse({
          telephone,
        }).unwrap();
      } catch (error) {
        setTelephoneInUse(true);
      }
    };
    fetch();
  }, [formValues.telephone]);

  const initialAddress =
    streetName && streetNo && postalCode && validated
      ? {
          streetName,
          streetNumber: streetNo.toString(),
          letterExtra: letterExtra || "",
          postalCode,
          postalArea: postalCodeCity,
          validated,
          floorNumber: floorNo,
          door: flatNo,
          apartmentName: apartmentName,
        }
      : undefined;

  const isApartmentInfoComplete = errors?.apartment === undefined;

  return (
    <AppShell title={intl.YOUR_PERSONAL_INFORMATION}>
      <AppShell.Meta />

      <Header step={3} />
      <AppShell.DiscountBanner cta="text" />

      <AppShell.Main grid noPadding>
        <FlexWrapper
          back={{ type: "hidden" }}
          close={{ type: "hidden" }}
          background="white"
          className="md:mt-16 md:mb-8"
        >
          <SignupProgress
            title={intl.SIGNUP_PROGRESS_TITLE}
            subTitle={intl.SIGNUP_PROGRESS_SUBTITLE}
            id="signup-progress-card"
          />

          <FormProvider {...methods}>
            <form onSubmit={handleSubmit(onSubmit)}>
              <div
                className="grid grid-cols-10 gap-4 px-4 mb-4 lg:mb-6"
                id="your-information-card"
              >
                {/* <h2 className="text-xl md:text-2xl col-span-full">
                  <strong>{intl.YOUR_PERSONAL_INFORMATION}</strong>
                </h2> */}
                <p className="text-base col-span-full">
                  <strong>{intl.YOUR_NAME}</strong>
                </p>

                <Input
                  className={clsx(
                    "col-span-full",
                    BRAND_NAME !== "LMK" && "hidden",
                  )}
                  {...register("ssn")}
                  name="ssn"
                  id="ssn"
                  inputMode="numeric"
                  label={intl.PERSONAL_NUMBER_LABEL}
                  maxLength={intl.LENGTH_OF_PERSONAL_NUMBER}
                  autoComplete="off"
                />

                <Input
                  className="col-span-5"
                  {...register("firstName")}
                  name="firstName"
                  label={intl.FIRST_NAME_LABEL}
                  autoFocus
                />
                <Input
                  className="col-span-5"
                  {...register("lastName")}
                  name="lastName"
                  label={intl.LAST_NAME_LABEL}
                />

                <hr className="col-span-full text-grey-2" />

                <p className="text-base col-span-full">
                  <strong>{intl.YOUR_ADDRESS}</strong>
                </p>

                <AddressForm
                  doNotDeliver={noDeliveryAvailable}
                  initialAddress={initialAddress}
                  city={city}
                />
              </div>

              <DeliveryInformation
                availableWeekAndYearTimeblocks={availableWeekAndYearTimeblocks}
                availableTimeblocks={
                  timeblocksQuery?.timeblocksData?.timeblocks || []
                }
                deliveryDetailsRef={deliveryDetailsRef}
                deliveryDetailsModalContent={deliveryDetailsModalContent}
                register={register}
                digitalKeyProvider={
                  formValues.validatedAddress?.digitalKeyProvider?.name
                }
                isApartmentAddress={isApartmentAddress}
                id="deliveries-delivery-information-card"
              />

              {sanityAddressContent && (
                <BlockContent
                  body={sanityAddressContent.content.body}
                  prose={{ disabled: true }}
                />
              )}
            </form>
          </FormProvider>
        </FlexWrapper>

        <CustomerExistsModal
          onClose={() => setTelephoneInUse(false)}
          isOpen={telephoneInUse}
          initialLoginMethod="telephone"
          initialTelephone={formValues.telephone}
        />
      </AppShell.Main>

      <CheckoutBar
        onBeforeGoToCheckout={handleSubmit(onSubmit)}
        disabled={!isValid || isSubmitting || !isApartmentInfoComplete}
      />
    </AppShell>
  );
};

const schema = yup
  .object({
    ssn: intl.SKIP_SSN_VALIDATION
      ? yup.string()
      : yup
          .string()
          .length(
            intl.LENGTH_OF_PERSONAL_NUMBER,
            intl.MUST_BE_EXACTLY_DIGITS(intl.LENGTH_OF_PERSONAL_NUMBER),
          )
          .matches(/^[0-9]*$/, intl.CAN_ONLY_CONTAIN_NUMBERS)
          .required(intl.MANDATORY_FIELD)
          .test({
            test: (value) => intl.IS_VALID_SSN(value),
            message: intl.PERSONAL_NUMBER_NOT_VALID,
          })
          .test({
            test: (ssn) => {
              // Is being checked in another test, skipping if it fails
              if (typeof ssn === "undefined") {
                return true;
              }
              return getAgeFromSwedishSSN(ssn) >= 18;
            },
            message: intl.MUST_BE_18_YEARS_OR_OLDER,
          })
          .test({
            test: yupCacheAllTests(async (ssn) => {
              // isValidSSN is being checked in another test, skipping if it fails to not make redundant api calls
              if (!intl.IS_VALID_SSN(ssn)) {
                return true;
              }

              try {
                await getStore()
                  .dispatch(
                    validateApi.endpoints.validateSSNInUse.initiate({
                      ssn,
                    }),
                  )
                  .unwrap();
                return true;
              } catch (err) {
                return false;
              }
            }),
            message: intl.PERSONAL_NUMBER_ALREADY_IN_USE,
          }),

    validatedAddress: yup.object({
      streetName: yup.string(),
      streetNumber: yup.string(),
      letterExtra: yup.string(),
      addressName: yup.string(),
      validated: yup.string(),
      addressGuid: yup.string(),
      addressCode: yup.string(),
      longitude: yup.number(),
      latitude: yup.number(),
      digitalKeyProvider: yup
        .object({
          addressDigitalKeyProviderId: yup.string(),
          name: yup.string(),
        })
        .nullable(),
      apartments: yup.array().of(
        yup.object({
          name: yup.string().nullable(),
          floor: yup.string().nullable(),
          door: yup.string().nullable(),
        }),
      ),
    }),

    apartment: yup.object({
      name: yup.string().nullable(),
      floor: yup.string().nullable(),
      door: yup.string().nullable(),
    }),

    streetName: yup.string(),
    streetNo: yup.number(),
    letterExtra: yup.string(),
    apartmentName: yup.string(),
    flatNo: yup.string().nullable(),
    floorNo: yup.string(),
    validated: yup.string(),
    addressUUID: yup.string(),
    addressCode: yup.string(),
    longitude: yup.number(),
    latitude: yup.number(),
    addressDigitalKeyProviderId: yup.string(),

    firstName: yup.string().required(intl.MANDATORY_FIELD),

    lastName: yup.string().required(intl.MANDATORY_FIELD),

    isHealthCheckSuccess: yup.boolean(),

    address: yup.string().when("healthCheck", {
      is: false,
      then: yup.string().required(intl.MANDATORY_FIELD),
    }),

    postalCode: yup
      .string()
      .matches(/^[0-9]*$/, intl.CAN_ONLY_CONTAIN_NUMBERS)
      .length(
        intl.LENGTH_OF_POSTALCODE,
        intl.MUST_BE_EXACTLY_DIGITS(intl.LENGTH_OF_POSTALCODE),
      )
      .required(intl.MANDATORY_FIELD)
      .test({
        test: async (postalCode) => {
          if (!isValidPostalCode(postalCode)) {
            return true;
          }

          try {
            const timeblock = await getStore()
              .dispatch(
                api.endpoints.timeblocksByRangeWithGeo.initiate({
                  postalCode,
                  range: 8,
                  timeblockTypeId: TIMEBLOCK_TYPE_IDS.NORMAL,
                }),
              )
              .unwrap();
            const { timeblocksByRangeWithGeo } = timeblock;
            const filteredAvailableWeeklyTimeblocks =
              filterAvailableWeeklyTimeblocks(timeblocksByRangeWithGeo.weeks);
            return filteredAvailableWeeklyTimeblocks.some((t) => t.timeblocks);
          } catch (error) {
            return false;
          }
        },
        message: intl.WE_CANNOT_DELIVER_HERE,
      })
      .required(),

    telephone: yup
      .string()
      .matches(/^[0-9]*$/, intl.CAN_ONLY_CONTAIN_NUMBERS)
      .required(intl.MANDATORY_FIELD)
      .test({
        test: (value) => isValidTelephoneNumber(value || ""),
        message: intl.MUST_BE_EXACTLY_DIGITS(intl.LENGTH_OF_TELEPHONE_NUMBER),
      })
      .test({
        test: async (value) => {
          // We need to duplicate the check here because yup runs validations asynchronously
          if (value === undefined || !isValidTelephoneNumber(value)) {
            return true;
          }

          try {
            await getStore()
              .dispatch(
                validateApi.endpoints.validateTelephoneInUse.initiate({
                  telephone: value,
                }),
              )
              .unwrap();
            return true;
          } catch (error) {
            console.error(error);
            return false;
          }
        },

        message: intl.TELEPHONE_NUMBER_ALREADY_IN_USE,
      })
      .optional(),

    doorCode: yup.string().optional(),

    deliveryInstructionsToDriver: yup.string().optional(),

    requiresPhysicalKey: yup.boolean().required(),
  })
  .required();
